import { apiClient } from './client';

export interface ScannerDevice {
  id: string;
  name: string;
  host: string;
  port: number;
  status: 'online' | 'offline' | 'busy';
  manufacturer: string;
  model: string;
  capabilities: string[];
  discoveryMethod?: string; // Nouveau champ pour indiquer le protocole de découverte
}

export interface ScanOptions {
  resolution?: '150' | '300' | '600';
  format?: 'JPEG' | 'PNG' | 'PDF' | 'TIFF';
  colorMode?: 'color' | 'grayscale' | 'black_white';
  duplex?: boolean;
}

export interface ScanResult {
  success: boolean;
  scanId: string;
  imageUrl: string;
  filename: string;
  size: number;
  format: string;
  resolution: string;
  timestamp: string;
}

export interface ScannerStatus {
  success: boolean;
  scannerId: string;
  status: 'online' | 'offline' | 'busy';
  isReady: boolean;
  timestamp: string;
}

class ScannerService {
  constructor() {
    console.log('🔧 [ScannerService] Service instancié');
  }

  /**
   * Découvrir les scanners disponibles sur le réseau
   */
  async discoverScanners(): Promise<{ success: boolean; scanners: ScannerDevice[]; source?: string; count?: number; timestamp?: string; error?: string }> {
    console.log('🔍 [ScannerService] Démarrage découverte scanners...');

    try {
      // Le client API gère automatiquement le timeout selon l'URL (90s pour /scanners/discover)
      const response = await apiClient.get('/scanners/discover');
      console.log('🔍 [ScannerService] Réponse reçue:', response);

      // S'assurer que la réponse contient les données attendues
      if (!response || typeof response !== 'object') {
        console.error('🔍 [ScannerService] Réponse invalide:', response);
        throw new Error('Réponse invalide du serveur');
      }

      // Vérifier si la réponse indique un succès
      if (response.success === false) {
        console.error('🔍 [ScannerService] Échec API:', response);
        throw new Error(response.message || 'Échec de la découverte des scanners');
      }

      // Retourner les données avec vérification
      const result = {
        success: response.success ?? true,
        scanners: Array.isArray(response.scanners) ? response.scanners : [],
        source: response.source || 'unknown',
        count: response.count || 0,
        timestamp: response.timestamp || new Date().toISOString()
      };

      console.log('🔍 [ScannerService] Découverte réussie:', result.scanners.length, 'scanners');
      return result;

    } catch (error: any) {
      console.error('🔍 [ScannerService] Erreur lors de la découverte:', error);

      // Si c'est une erreur réseau, la propager
      if (error.message && error.message.includes('Network Error')) {
        throw new Error('Erreur de connexion réseau - vérifiez que le serveur est démarré');
      }

      // Pour toute autre erreur, retourner un résultat d'erreur
      return {
        success: false,
        scanners: [],
        source: 'network_error',
        count: 0,
        timestamp: new Date().toISOString(),
        error: error.message || 'Erreur inconnue'
      };
    }
  }

  /**
   * Démarrer un scan sur un scanner spécifique
   */
  async startScan(scannerId: string, options?: ScanOptions): Promise<ScanResult> {
    console.log(`📷 [ScannerService] Démarrage scan sur ${scannerId} avec options:`, options);

    const response = await apiClient.post(`/scanners/${scannerId}/scan`, { options });
    const data = response.data;

    console.log(`📷 [ScannerService] Réponse reçue:`, data);

    // Vérifier que la réponse contient les champs requis
    if (!data || typeof data !== 'object') {
      console.error('📷 [ScannerService] Réponse invalide - pas un objet:', data);
      throw new Error('Réponse invalide du serveur');
    }

    if (data.success !== true) {
      console.error('📷 [ScannerService] Échec du scan:', data);
      throw new Error(data.error || data.message || 'Échec du scan');
    }

    if (!data.imageUrl || !data.filename) {
      console.error('📷 [ScannerService] Données manquantes:', data);
      throw new Error('Données de scan incomplètes');
    }

    console.log(`📷 [ScannerService] Scan réussi: ${data.filename} (${data.size} bytes)`);
    return data;
  }

  /**
   * Obtenir le statut d'un scanner
   */
  async getScannerStatus(scannerId: string): Promise<ScannerStatus> {
    const response = await apiClient.get(`/scanners/${scannerId}/status`);
    return response.data;
  }

  /**
   * Annuler un scan en cours
   */
  async cancelScan(scannerId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`/scanners/${scannerId}/scan`);
    return response.data;
  }

  /**
   * Tester la connexion IPP d'un scanner
   */
  async testIppConnection(scannerId: string): Promise<{ success: boolean; message?: string; capabilities?: any; error?: string }> {
    // Le client API gère automatiquement le timeout (30s pour /test-ipp)
    const response = await apiClient.post(`/scanners/${scannerId}/test-ipp`, {});
    return response.data;
  }

  /**
   * Diagnostiquer un scanner en détail
   */
  async diagnoseScanner(scannerId: string): Promise<{
    success: boolean;
    diagnosis?: {
      scannerInfo: any;
      networkTests: {
        ping: boolean;
        ippConnection: boolean;
        capabilities: any;
      };
      recommendations: string[];
      timestamp: string;
    };
    error?: string;
  }> {
    // Le client API gère automatiquement le timeout (20s pour /diagnose)
    const response = await apiClient.get(`/scanners/${scannerId}/diagnose`);
    return response.data;
  }
}

export const scannerService = new ScannerService();